import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, FileText, DollarSign, Wallet, Scale } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_COLORS = {
  'Open': 'bg-accent/20 text-accent',
  'Pending Docs': 'bg-warning-bg text-warning-text',
  'Negotiation': 'bg-blue-100 text-blue-700',
  'Settled': 'bg-success-bg text-success-text',
  'Paid in Full': 'bg-green-100 text-green-800',
  'Closed': 'bg-gray-200 text-gray-700'
};

const formatCurrency = (value) => `$${(value || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;

const claimSettlement = (claim) =>
  claim.actual_loss_settlement > 0 ? claim.actual_loss_settlement : claim.expected_loss_settlement;

export const CatEventDetailPage = () => {
  const { catEventId } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [summary, setSummary] = useState(null);
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchEventDetails = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/cat-events/${catEventId}`);
      setEvent(response.data.event);
      setSummary(response.data.summary);
      setClaims(response.data.claims);
    } catch (error) {
      toast.error('Failed to load CAT event details');
    } finally {
      setLoading(false);
    }
  }, [catEventId]);

  useEffect(() => {
    fetchEventDetails();
  }, [fetchEventDetails]);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-screen" data-testid="loading-spinner">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (!event) {
    return (
      <DashboardLayout>
        <div className="p-8">
          <p className="text-foreground-muted">CAT event not found</p>
        </div>
      </DashboardLayout>
    );
  }

  const summaryCards = [
    { label: 'Assigned Claims', value: summary.claim_count, icon: FileText, isCurrency: false },
    { label: 'Total Settlement Value', value: summary.total_settlement, icon: DollarSign, isCurrency: true },
    { label: 'Total Fees Collected', value: summary.total_fees_collected, icon: Wallet, isCurrency: true },
    { label: 'Total Fee Balance', value: summary.total_fee_balance, icon: Scale, isCurrency: true }
  ];

  return (
    <DashboardLayout>
      <div className="p-8" data-testid="cat-event-detail-page">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button variant="ghost" onClick={() => navigate('/cat-events')} data-testid="back-to-cat-events">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to CAT Events
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-4xl font-bold font-sans text-foreground tracking-tight" data-testid="cat-event-code">
                {event.cat_code}
              </h1>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${event.status === 'Open' ? 'bg-success-bg text-success-text' : 'bg-gray-200 text-gray-700'}`} data-testid="cat-event-status">
                {event.status}
              </span>
            </div>
            <p className="text-foreground-muted mt-1">
              {event.event_type} • Occurred {event.event_date}
            </p>
          </div>
        </div>

        {event.description && (
          <Card className="p-4 border border-border shadow-sm mb-8">
            <p className="text-sm text-foreground whitespace-pre-wrap" data-testid="cat-event-description">{event.description}</p>
          </Card>
        )}

        {/* Financial Roll-up */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-2">
          {summaryCards.map((card) => {
            const Icon = card.icon;
            return (
              <Card key={card.label} className="p-6 border border-border shadow-sm" data-testid={`summary-${card.label.toLowerCase().replace(/\s+/g, '-')}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-foreground-muted">{card.label}</p>
                    <p className="text-2xl font-bold font-mono text-foreground mt-1">
                      {card.isCurrency ? formatCurrency(card.value) : card.value}
                    </p>
                  </div>
                  <Icon className="w-8 h-8 text-accent/60" />
                </div>
              </Card>
            );
          })}
        </div>
        <p className="text-xs text-foreground-muted mb-8">Financial totals exclude archived claims.</p>

        {/* Assigned Claims */}
        <Card className="border border-border shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-border">
            <h3 className="text-xl font-semibold font-sans text-foreground">Assigned Claims</h3>
          </div>
          {claims.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="cat-event-claims-table">
                <thead className="bg-slate-50 text-slate-500 font-medium uppercase tracking-wider text-xs border-b border-border">
                  <tr>
                    <th className="px-6 py-4 text-left">Claim ID</th>
                    <th className="px-6 py-4 text-left">Customer</th>
                    <th className="px-6 py-4 text-left">Status</th>
                    <th className="px-6 py-4 text-right">Settlement Value</th>
                    <th className="px-6 py-4 text-right">Fees Collected</th>
                    <th className="px-6 py-4 text-right">Fee Balance</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {claims.map((claim) => (
                    <tr
                      key={claim.claim_id}
                      className="hover:bg-slate-50/50 transition-colors cursor-pointer"
                      onClick={() => navigate(`/claims/${claim.claim_id}`)}
                      data-testid={`cat-claim-row-${claim.claim_id}`}
                    >
                      <td className="px-6 py-4 font-mono text-xs font-medium text-accent hover:underline">
                        {claim.claim_id}
                      </td>
                      <td className="px-6 py-4 text-foreground">
                        {claim.customer_name || 'No Customer'}
                        {claim.archived && (
                          <span className="ml-2 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">Archived</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[claim.status] || 'bg-gray-100 text-gray-700'}`}>
                          {claim.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right font-mono text-xs text-foreground">
                        {formatCurrency(claimSettlement(claim))}
                      </td>
                      <td className="px-6 py-4 text-right font-mono text-xs text-success-text">
                        {formatCurrency(claim.amount_of_fee_paid)}
                      </td>
                      <td className="px-6 py-4 text-right font-mono text-xs">
                        <span className={claim.fee_balance > 0 ? 'text-destructive font-semibold' : 'text-success-text'}>
                          {formatCurrency(claim.fee_balance)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12" data-testid="no-assigned-claims">
              <p className="text-foreground-muted">No claims assigned to this CAT event yet</p>
            </div>
          )}
        </Card>
      </div>
    </DashboardLayout>
  );
};
